import {
  useState,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";

//Component
import Attachments from "./Attachment/Attachment";
import CheckboxWithLabel from "./CheckboxWithLabel";
import CustomAutoComplete from "./CustomAutoComplete";
import IncidentDetailNarrative from "./IncidentDetailNarrative";
import OffensesTabs from "./OffensesTabs";
import SingleDropDown from "./SingleDropDown";
import WrappedKeyboardDateTimePicker from "./WrappedKeyboardDateTimePicker";
import {
  FormControl,
  TextField,
  Typography,
  CircularProgress,
  TextareaAutosize,
  FormLabel,
  Box,
  Paper,
  IconButton,
} from "@material-ui/core";

//Contexts
import { UserContext } from "../Contexts/UserContext";
import { SetupContext } from "../Contexts/SetupContext";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";

//Mutations

//Styling
import { useStyles } from "../Styles/styles";
import addButton from "../Assets/addButton.png";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

//Utils
import {
  formatOfficerName,
  localGetData,
  parseDateFlexible,
  pickPreferredCommonCallType,
} from "../utils/utils";
import { MutationContext } from "../Contexts/MutationContext";
import {
  createEmptyNarrative,
  createEmptyIncidentDetail,
  createEmptyIncidentOffense,
} from "../utils/EmptyObjects";
import { shouldBeLocked } from "../utils/Roles";
import { useCookies } from "react-cookie";
import { getValueFromEventValue } from "../utils/getValueFromEventValue";

import moment from "moment";
import { getPoldTrigger } from "../utils/getPoldTrigger";

const stateToMutation = {
  occurredFrom: "occurredFrom",
  occurredTo: "occurredTo",
  investigatingOfficer: "investigatingOfficerId",
  searchType: "searchTypeId",
  physicalEvidenceText: "physicalEvidenceText",
  mediaSummary: "mediaSummary",
  specialVictimsUnit: "specialVictimsUnitId",
  troFroExists: "troFroExists",
  alcohol: "alcohol",
  nineOneOneCall: "nineOneOneCall",
  medicalRelease: "medicalRelease",
  audioRecordings: "audioRecordings",
  dcfNotified: "dcfNotified",
  crisisServiceInvolved: "crisisServiceInvolved",
  swabbings: "swabbings",
  siuContacted: "siuContacted",
  videoRecordings: "videoRecordings",
  photosTaken: "photosTaken",
  printsLifted: "printsLifted",
  diagrams: "diagrams",
  clothingEvidence: "clothingEvidence",
  k9: "k9",
  miranda: "miranda",
  otherPhysicalEvidence: "otherPhysicalEvidence",
  crimeSceneProcessed: "crimeSceneProcessed",
  lprUsed: "lprUsed",
  secondaryCallType: "secondaryCallTypeId",
};

const IncidentDetail = ({
  piss,
  incidentIndex,
  incident,
  incidentId,
  incidentUuid,
  incidentDetail,
  incidentDetailError,
  incidentDetailLoading,
  incidentOriId,
  primaryOfficer,
  canEditNarrative,
  userRoles,
  disabled = undefined,
  memoizedIsOnIncident,
  _incidentDetailIsMounted,
  setPausePolling,
  setPopper,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );
  const classes = useStyles();
  const { setup } = useContext(SetupContext);
  const {
    allOfficers,
    activeOfficersError,
    activeOfficersLoading,
    user,
    nightMode,
    onlineState,
  } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { updateIncidentDetail, throttledAddOrUpdateIncidentDetailNarrative } =
    useContext(MutationContext);
  const {
    UPDATE_INCIDENT_DETAIL,
    ADD_INCIDENT_DETAIL_NARRATIVE,
    UPDATE_INCIDENT,
  } = INCIDENT_ACTIONS;
  const [cookies] = useCookies();
  const { searchTypes, specialVictimsUnits } = setup.setupLists;

  const localStoreSpecificSetup =
    localGetData(`specific-setup-${incidentOriId}`) || {};
  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { incidentNarrativeTypes, classifications } = specificSetupLists || {};

  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [mediaNarrativeHeight, setMediaNarrativeHeight] = useState("30vh");

  const poldOptOut = useMemo(() => {
    return incident?.ori?.poldOptOut || false;
  }, [incident]);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    if (
      !onlineState &&
      !incidentDetail &&
      import.meta.env.VITE_FORCE_ONLINE !== "true" &&
      !memoizedLocked
    ) {
      let emptyIncidentDetail = createEmptyIncidentDetail(
        incident?.incidentDetailId,
      );
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incidentId,
          uuid: incidentUuid,
          property: "incidentDetail",
          value: emptyIncidentDetail,
        },
      });
    }
  }, [
    UPDATE_INCIDENT,
    incident?.incidentDetailId,
    incidentDetail,
    incidentDispatch,
    incidentId,
    incidentUuid,
    memoizedLocked,
    onlineState,
  ]);

  useEffect(() => {
    _incidentDetailIsMounted.current = true;
    return () => {
      // ComponentWillUnmount in Class Component
      _incidentDetailIsMounted.current = false;
    };
  }, [_incidentDetailIsMounted]);

  const handleDateChange = useCallback(
    (value, name, date) => {
      const parsedDate = parseDateFlexible(date);
      let fieldToUpdate = stateToMutation[name];
      if (!parsedDate) {
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: {
            incidentDetailId: incidentDetail?.id
              ? incidentDetail.id
              : incidentDetail?.uuid,
            incidentIndex: incidentIndex,
            property: name,
            value: "",
          },
        });
        updateIncidentDetail({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId,
              incidentDetailId: incidentDetail?.id,
              [fieldToUpdate]: "",
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      } else if (parsedDate.isValid()) {
        const localTimeString = parsedDate.format("YYYY-MM-DD HH:mm:ss");
        const serverTimeFormat = parsedDate
          .utc()
          .format("YYYY-MM-DD HH:mm:ss UTC");
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: {
            incidentDetailId: incidentDetail?.id
              ? incidentDetail.id
              : incidentDetail?.uuid,
            incidentIndex: incidentIndex,
            property: name,
            value: localTimeString,
          },
        });
        updateIncidentDetail({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId,
              incidentDetailId: incidentDetail?.id,
              [fieldToUpdate]: serverTimeFormat,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      }
    },
    [
      UPDATE_INCIDENT_DETAIL,
      incidentDetail.id,
      incidentDetail?.uuid,
      incidentDispatch,
      incidentId,
      incidentIndex,
      updateIncidentDetail,
      user?.id,
    ],
  );

  const handleChange = (event, submit = true) => {
    if (event.target.type === "checkbox") {
      const bool = event.target.checked;
      const name = event.target.value;
      const fieldToUpdate = stateToMutation[name];
      incidentDispatch({
        type: UPDATE_INCIDENT_DETAIL,
        payload: {
          incidentDetailId: incidentDetail?.id
            ? incidentDetail.id
            : incidentDetail?.uuid,
          incidentIndex: incidentIndex,
          property: name,
          value: bool,
        },
      });
      updateIncidentDetail({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incidentId,
            incidentDetailId: incidentDetail?.id,
            [fieldToUpdate]: bool,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      if (name === "alcohol" && bool === true) {
        const incidentCopy = {
          ...incident,
          incidentDetail: { ...incidentDetail, alcohol: true },
        };
        const { needsPoldWarning } = getPoldTrigger({
          incident: incidentCopy,
          poldOptOut,
        });
        if (needsPoldWarning && setPopper) {
          setPopper({
            open: true,
            severity: "info",
            content:
              "Please fill out the POLD field in Initial Call Information.",
          });
        }
      }
    } else {
      const name = event.target.name || null;
      const value = event.target.value;
      const mutationValue = getValueFromEventValue(event.target.value);
      const fieldToUpdate = stateToMutation[name];
      if (name === "secondaryCallType") {
        const secondaryCommonCallType = value?.commonCallType;
        const classificationCommonCallType =
          incident?.classification?.commonCallType;
        const resolvedCommonCallType = pickPreferredCommonCallType(
          secondaryCommonCallType,
          classificationCommonCallType,
        );
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: "commonCallType",
            value: resolvedCommonCallType,
          },
        });
      }
      incidentDispatch({
        type: UPDATE_INCIDENT_DETAIL,
        payload: {
          incidentDetailId: incidentDetail?.id
            ? incidentDetail.id
            : incidentDetail?.uuid,
          incidentIndex: incidentIndex,
          property: name,
          value: value,
        },
      });
      submit &&
        updateIncidentDetail({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId,
              incidentDetailId: incidentDetail?.id,
              [fieldToUpdate]: mutationValue,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      if (!name) {
        alert(
          `Please add a name field that matches the state name to the component that was just updated`,
        );
        return null;
      }
    }
  };

  const handleAutoCompleteChange = useCallback(
    (value, name) => {
      const fieldToUpdate = stateToMutation[name];
      incidentDispatch({
        type: UPDATE_INCIDENT_DETAIL,
        payload: {
          incidentDetailId: incidentDetail?.id
            ? incidentDetail.id
            : incidentDetail?.uuid,
          incidentIndex: incidentIndex,
          property: name,
          value: value,
        },
      });
      updateIncidentDetail({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incidentId,
            incidentDetailId: incidentDetail?.id,
            [fieldToUpdate]: value?.id ? value.id : value,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    },
    [
      UPDATE_INCIDENT_DETAIL,
      incidentDetail.id,
      incidentDetail?.uuid,
      incidentDispatch,
      incidentId,
      incidentIndex,
      updateIncidentDetail,
      user?.id,
    ],
  );

  const handleAddNarrativeToIncident = useCallback(
    user => {
      //leave the officer as empty until someone types in there
      const emptyNarrative = createEmptyNarrative(user);

      incidentDispatch({
        type: ADD_INCIDENT_DETAIL_NARRATIVE,
        payload: { incidentIndex: incidentIndex, narrative: emptyNarrative },
      });
      throttledAddOrUpdateIncidentDetailNarrative({
        variables: {
          input: {
            incidentId: incidentId,
            incidentNarrativeId: emptyNarrative.uuid,
            originatorId: user?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    },
    [
      ADD_INCIDENT_DETAIL_NARRATIVE,
      incidentDispatch,
      incidentId,
      incidentIndex,
      throttledAddOrUpdateIncidentDetailNarrative,
    ],
  );

  useEffect(() => {
    if (
      incidentDetail != null &&
      Object.prototype.hasOwnProperty.call(incidentDetail, "incidentNarratives") &&
      !!incidentDetail.incidentNarratives &&
      (!incidentDetail?.incidentNarratives ||
        incidentDetail?.incidentNarratives.length === 0) &&
      !memoizedLocked
    ) {
      handleAddNarrativeToIncident();
    }
  }, [
    handleAddNarrativeToIncident,
    incidentDetail,
    incidentDetailLoading,
    memoizedLocked,
  ]);

  useEffect(() => {
    if (
      incidentDetail != null &&
      Object.prototype.hasOwnProperty.call(incidentDetail, "incidentOffenses") &&
      !!incidentDetail.incidentOffenses &&
      (!incidentDetail?.incidentOffenses ||
        incidentDetail?.incidentOffenses.length === 0) &&
      !memoizedLocked
    ) {
      const emptyIncidentOffense = createEmptyIncidentOffense();
      incidentDispatch({
        type: UPDATE_INCIDENT_DETAIL,
        payload: {
          incidentIndex: incidentIndex,
          property: "incidentOffenses",
          value: [emptyIncidentOffense],
        },
      });
    }
  }, [
    incidentDetail,
    UPDATE_INCIDENT_DETAIL,
    incidentIndex,
    incidentDispatch,
    memoizedLocked,
  ]);

  useEffect(() => {
    if (
      incidentDetail != null &&
      Object.prototype.hasOwnProperty.call(incidentDetail, "investigatingOfficer") &&
      !incidentDetail?.investigatingOfficer &&
      primaryOfficer &&
      !memoizedLocked &&
      memoizedIsOnIncident
    ) {
      handleAutoCompleteChange(primaryOfficer, "investigatingOfficer");
    }
  }, [
    handleAutoCompleteChange,
    incidentDetail,
    memoizedIsOnIncident,
    memoizedLocked,
    primaryOfficer,
  ]);

  useEffect(() => {
    if (
      incidentDetail != null &&
      Object.prototype.hasOwnProperty.call(incidentDetail, "occurredFrom") &&
      !incidentDetail?.occurredFrom &&
      incident?.callTime &&
      !memoizedLocked &&
      memoizedIsOnIncident
    ) {
      handleDateChange(
        null,
        "occurredFrom",
        moment.utc(incident?.callTime, "YYYY-MM-DD HH:mm:ss UTC").local(),
      );
    }
  }, [
    handleDateChange,
    incident?.callTime,
    incidentDetail,
    memoizedIsOnIncident,
    memoizedLocked,
  ]);

  useEffect(() => {
    if (
      incidentDetail != null &&
      Object.prototype.hasOwnProperty.call(incidentDetail, "occurredTo") &&
      !incidentDetail?.occurredTo &&
      incident?.callTime &&
      !memoizedLocked &&
      memoizedIsOnIncident
    ) {
      handleDateChange(
        null,
        "occurredTo",
        moment.utc(incident?.callTime, "YYYY-MM-DD HH:mm:ss UTC").local(),
      );
    }
  }, [
    handleDateChange,
    incident?.callTime,
    incidentDetail,
    memoizedIsOnIncident,
    memoizedLocked,
  ]);

  if (incidentDetailError)
    return <p>Error: {JSON.stringify(incidentDetailError)}</p>;
  if (incidentDetailLoading && onlineState) return <CircularProgress />;
  if (
    incidentDetail ||
    !onlineState ||
    import.meta.env.VITE_FORCE_ONLINE === "true"
  ) {
    return (
      <Paper
        id="top-incident-detail-div"
        style={{ maxWidth: "100%" }}
        autoComplete="off"
      >
        <div style={{ border: "1px solid black", padding: "5px" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "space-between",
            }}
          >
            <WrappedKeyboardDateTimePicker
              ampm={false}
              className={classes.smallMatchMargin}
              clearable={true}
              data-cy="occurred-from-picker"
              disabled={memoizedLocked}
              format="MM/DD/YYYY HH:mm"
              InputLabelProps={{ shrink: true }}
              label="Occurred From"
              name="occurredFrom"
              onBlur={() => setPausePolling(false)}
              onChange={handleDateChange}
              onFocus={() => setPausePolling(true)}
              showTodayButton={true}
              value={parseDateFlexible(incidentDetail?.occurredFrom)}
            />
            <WrappedKeyboardDateTimePicker
              ampm={false}
              className={classes.smallMatchMargin}
              clearable={true}
              data-cy="occurred-to-picker"
              disabled={memoizedLocked}
              format="MM/DD/YYYY HH:mm"
              InputLabelProps={{ shrink: true }}
              label="Occurred To"
              name="occurredTo"
              onBlur={() => setPausePolling(false)}
              onChange={handleDateChange}
              onFocus={() => setPausePolling(true)}
              showTodayButton={true}
              value={parseDateFlexible(incidentDetail?.occurredTo)}
            />
            <CustomAutoComplete
              title={"Investigating\xa0Officer"}
              loading={activeOfficersLoading}
              options={allOfficers}
              addMargin={false}
              error={activeOfficersError}
              value={
                incidentDetail?.investigatingOfficer
                  ? incidentDetail.investigatingOfficer
                  : null
              }
              stateName={"investigatingOfficer"}
              updateFields={handleAutoCompleteChange}
              nameFormatter={formatOfficerName}
              userRoles={userRoles}
              {...props}
            />
          </div>
          <div className={classes.flexRow} style={{ alignItems: "center" }}>
            <button
              type="button"
              onClick={
                !memoizedLocked
                  ? () => handleAddNarrativeToIncident(user)
                  : null
              }
              disabled={memoizedLocked}
              style={{ background: "none", border: 0, padding: 0 }}
            >
              <img
                data-cy={"add-narrative-button"}
                src={addButton}
                className={classes.pngButton}
                alt="Add Narrative"
              />
            </button>
            <Typography
              style={{ margin: "0 8px", fontWeight: "bold" }}
              data-cy="person-header"
            >
              Narratives
            </Typography>
          </div>
          <div
            style={{ width: "100%" }}
            id="incident-detail-container"
            data-cy="incident-detail-container"
          >
            {incidentDetail?.incidentNarratives?.map((narrative, index) => {
              return (
                <IncidentDetailNarrative
                  canEditNarrative={canEditNarrative}
                  divStyle={classes.greyDiv}
                  formStyle={classes.formControl}
                  fabStyle={classes.fabButton}
                  narrative={narrative}
                  numOfNarratives={incidentDetail?.incidentNarratives.length}
                  narrativeIndex={index}
                  key={narrative.id}
                  narrativeTypes={incidentNarrativeTypes}
                  incidentId={incidentId}
                  incidentIndex={incidentIndex}
                  addNarrativeToIncident={handleAddNarrativeToIncident}
                  userRoles={userRoles}
                  incidentOriId={incidentOriId}
                  {...props}
                />
              );
            })}
          </div>
          <Attachments
            parentId={incident?.id || incidentId}
            parentName={incident?.incidentNumber || ""}
            uploadClass={"Incident"}
            secondarySiblingIds={user?.secondarySiblingIds}
            memoizedLocked={memoizedLocked}
            userId={user?.id}
            primaryOfficerId={primaryOfficer?.id}
            userRoles={userRoles}
            online={onlineState}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.troFroExists || false}
            onChange={handleChange}
            stateName={"troFroExists"}
            title={"TRO/FRO Exists"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.alcohol || false}
            onChange={handleChange}
            stateName={"alcohol"}
            title={"Alcohol Involved"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.nineOneOneCall || false}
            onChange={handleChange}
            stateName={"nineOneOneCall"}
            title={"911 Call Exists"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.medicalRelease || false}
            onChange={handleChange}
            stateName={"medicalRelease"}
            title={"Medical Release"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.audioRecordings || false}
            onChange={handleChange}
            stateName={"audioRecordings"}
            title={"Audio Recordings"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.dcfNotified || false}
            onChange={handleChange}
            stateName={"dcfNotified"}
            title={"DCF Notified"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.crisisServiceInvolved || false}
            onChange={handleChange}
            stateName={"crisisServiceInvolved"}
            title={"Crisis SVC Involved"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.swabbings || false}
            onChange={handleChange}
            stateName={"swabbings"}
            title={"Swabbings"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.siuContacted || false}
            onChange={handleChange}
            stateName={"siuContacted"}
            title={"SIU Contacted"}
            userRoles={userRoles}
            {...props}
          />
          <SingleDropDown
            handleChange={handleChange}
            stateName={"specialVictimsUnit"}
            value={
              incidentDetail?.specialVictimsUnit
                ? incidentDetail?.specialVictimsUnit
                : ""
            }
            options={specialVictimsUnits}
            title={"SVU Contact"}
            classStyle={classes.formControl}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />

          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.videoRecordings || false}
            onChange={handleChange}
            stateName={"videoRecordings"}
            title={"Video Recordings"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.photosTaken || false}
            onChange={handleChange}
            stateName={"photosTaken"}
            title={"Photos Taken"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.printsLifted || false}
            onChange={handleChange}
            stateName={"printsLifted"}
            title={"Prints Lifted"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.diagrams || false}
            onChange={handleChange}
            stateName={"diagrams"}
            title={"Diagrams"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.clothingEvidence || false}
            onChange={handleChange}
            stateName={"clothingEvidence"}
            title={"Clothing Evidence"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.k9 || false}
            onChange={handleChange}
            stateName={"k9"}
            title={"K9"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.miranda || false}
            onChange={handleChange}
            stateName={"miranda"}
            title={"Miranda Warning"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.otherPhysicalEvidence || false}
            onChange={handleChange}
            stateName={"otherPhysicalEvidence"}
            title={"Other Evidence"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.crimeSceneProcessed || false}
            onChange={handleChange}
            stateName={"crimeSceneProcessed"}
            title={"Crime Scene Processed"}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={incidentDetail?.lprUsed || false}
            onChange={handleChange}
            stateName={"lprUsed"}
            title={"Lpr Used"}
            userRoles={userRoles}
            {...props}
          />
          <SingleDropDown
            handleChange={handleChange}
            stateName={"searchType"}
            value={incidentDetail?.searchType ? incidentDetail.searchType : ""}
            options={searchTypes}
            title={"Evid. Search Conducted"}
            classStyle={classes.formControl}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <FormControl variant="standard" className={classes.formControl}>
            <TextField
              data-cy="physicalEvidenceText"
              inputProps={{ "data-cy": "physicalEvidenceText-input" }}
              InputLabelProps={{
                shrink: true,
                "data-cy": "physicalEvidenceText-input-label",
              }}
              margin="dense"
              id="physicalEvidenceText-id"
              label="Physical Evidence"
              value={incidentDetail?.physicalEvidenceText || ""}
              onChange={e => handleChange(e, false)}
              onBlur={e => handleChange(e, true)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.target.blur();
                }
              }}
              name="physicalEvidenceText"
              disabled={memoizedLocked}
            />
          </FormControl>
          <Box
            display="flex"
            flexDirection="column"
            flexGrow="2"
            margin="auto 0"
          >
            <Box display="flex" flexDirection="row" alignItems="center">
              <FormLabel data-cy={"new-narrative-label"} component="label">
                {"Media\xa0Summary"}
              </FormLabel>
              <IconButton
                edge="start"
                onClick={() =>
                  mediaNarrativeHeight === "30vh"
                    ? setMediaNarrativeHeight("fit-content")
                    : setMediaNarrativeHeight("30vh")
                }
                style={{ margin: 0 }}
              >
                {mediaNarrativeHeight === "30vh" ? (
                  <ExpandMore />
                ) : (
                  <ExpandLess />
                )}
              </IconButton>
            </Box>

            <TextareaAutosize
              // disabled={narrative?.employee?.id && narrative?.employee?.id.toString() !== user?.id.toString() ? true : false}
              className={
                cookies[`mp-userId-${user?.id}-night-mode`] === "true"
                  ? classes.textAreaDark
                  : classes.textAreaLight
              }
              disabled={memoizedLocked}
              data-cy="mediaSummary"
              style={{
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontSize: "1rem",
                maxHeight: mediaNarrativeHeight,
                overflow: "auto",
              }}
              // style={{ fontFamily: "Roboto, Helvetica, Arial, sans-serif", fontSize: "1rem" }}
              margin="dense"
              id="mediaSummary-id"
              value={incidentDetail?.mediaSummary || ""}
              onChange={e => handleChange(e, false)}
              onBlur={e => handleChange(e, true)}
              minRows={2}
              name="mediaSummary"
              // placeholder="Media Summary"
              // onChange={handleChange}
            />
          </Box>
          <SingleDropDown
            handleChange={handleChange}
            stateName={"secondaryCallType"}
            value={
              incidentDetail?.secondaryCallType
                ? incidentDetail.secondaryCallType
                : ""
            }
            options={classifications}
            title={"Secondary\xa0Call\xa0Type"}
            classStyle={classes.formControl}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
        </div>
        <OffensesTabs
          nightMode={nightMode}
          incidentId={incidentId}
          incidentIndex={incidentIndex}
          incidentOffenses={incidentDetail?.incidentOffenses || []}
          piss={piss}
          incidentOriId={incidentOriId}
          userRoles={userRoles}
          setPopper={setPopper}
          {...props}
        />
        {/* </div> */}
      </Paper>
    );
  } else {
    return <CircularProgress />;
  }
};

export default IncidentDetail;
